/** Types for `apps/integrations` API. Responses omit secrets; `credentials_hint` is masked only. */

export type IntegrationCategory =
  | 'address'
  | 'vehicle'
  | 'erp'
  | 'payments'
  | 'email'
  | 'sms'
  | 'ecommerce'
  | 'public_api'
  | 'rewards'

export type IntegrationProvider =
  | 'goaddress'
  | 'portalgo'
  | 'fetchify'
  | 'smartystreets'
  | 'dvla'
  | 'sap'
  | 'stripe'
  | 'resend'
  | 'twilio'
  | 'shopify'
  | 'woocommerce'
  | 'occonnect'
  | 'tango'

export type IntegrationHealth =
  | 'healthy'
  | 'degraded'
  | 'error'
  | 'unknown'

/** `platform` = company-managed key; `byo` = tenant’s encrypted credentials on the row. */
export type IntegrationSource = 'platform' | 'byo'

export interface Integration {
  uuid: string
  category: IntegrationCategory
  provider: IntegrationProvider
  source: IntegrationSource
  is_active: boolean
  health_status: IntegrationHealth
  last_health_check_at: string | null
  created_at: string
  updated_at: string
}

export interface IntegrationDetail extends Integration {
  credentials_hint: string
  /** Non-secret provider config (identifiers, environment, defaults). */
  config?: Record<string, unknown>
  /** Other providers paused when activating this one (POST/PATCH). Often [] or omitted on GET. */
  deactivated_providers?: IntegrationProvider[]
}

/** One row from `GET /api/integrations/catalog/`. Same `provider` can appear twice (platform vs byo). */
export interface CatalogEntry {
  provider: IntegrationProvider
  category: IntegrationCategory
  source: IntegrationSource
  display_name: string
  description: string
  requires_flag: string | null
  /** Tenant can connect now (flag + implemented). */
  available: boolean
  /** Org already has a matching Integration. */
  connected: boolean
  /** Set when `connected`. */
  integration_uuid: string | null
  /** Adapter not shipped yet. */
  coming_soon: boolean
  /** BYO credential fields to render in the Connect form. Empty for PLATFORM entries. */
  credentials_schema?: Record<string, string>
  /** Non-secret config fields rendered alongside credentials (e.g. rewards). */
  config_schema?: Record<string, string>
}

/** `POST /api/integrations/`. Omit credentials for `source: platform`; default source is BYO. */
export interface IntegrationCreatePayload {
  category: IntegrationCategory
  provider: IntegrationProvider
  source?: IntegrationSource
  credentials?: Record<string, string>
  config?: Record<string, unknown>
  is_active?: boolean
}

/** `PATCH /api/integrations/<uuid>/`. */
export interface IntegrationUpdatePayload {
  credentials?: Record<string, string>
  config?: Record<string, unknown>
  is_active?: boolean
}

/** `POST /api/integrations/<uuid>/test/`. */
export interface TestConnectionResult {
  healthy: boolean
}

// ── Rewards connect-options probe (`POST /api/integrations/rewards/options/`) ──

export interface RewardsCustomer {
  customer_identifier: string
  display_name?: string
  status?: string
}

export interface RewardsAccount {
  account_identifier: string
  display_name?: string
  currency_code?: string
  current_balance?: number | null
  status?: string
}

export interface RewardsCatalogItem {
  utid: string
  brand_key?: string
  reward_name?: string
  currency?: string
  value_type?: string
  min_value?: number | null
  max_value?: number | null
  status?: string
}

export interface RewardsEmailTemplate {
  etid: string
  name?: string
}

export interface RewardsOptions {
  customers: RewardsCustomer[]
  accounts: RewardsAccount[]
  catalog: RewardsCatalogItem[]
  catalog_truncated?: boolean
  error?: string
}

export interface RewardsOptionsPayload {
  platform_id: string
  auth_token: string
  environment?: string
}

// ── Reward rules (`/api/integrations/rewards/rules/`) ──

export interface RewardTriggerOption {
  workflow_key: string
  state_key: string
  state_label: string
}

export interface RewardRule {
  workflow_key: string
  state_key: string
  state_label: string
  /** Stable per-state rule key (slug). Many rules can share a state. */
  key: string
  label: string
  config: Record<string, unknown>
  when?: unknown
}

export interface RewardRulesResponse {
  rules: RewardRule[]
  trigger_options: RewardTriggerOption[]
}

/**
 * Dropdown data for the rule builder - accounts, reward catalog, and email
 * templates from the connected integration. A live provider read, so it is
 * fetched lazily (only when the rule dialog opens), NOT on the rules list.
 */
export interface RewardRoutingOptions {
  accounts: RewardsAccount[]
  catalog: RewardsCatalogItem[]
  templates: RewardsEmailTemplate[]
}

export interface RewardRulePayload {
  workflow_key: string
  state_key: string
  key?: string
  label?: string
  config?: Record<string, unknown>
  when?: unknown
}

/** Funded-account balance for the org's active rewards integration. */
export interface RewardsBalance {
  available: boolean
  currency?: string
  current_balance?: number
  status?: string
}

export type RewardIssuanceStatus =
  | 'pending'
  | 'ordered'
  | 'delivered'
  | 'redeemed'
  | 'failed'
  | 'cancelled'

/**
 * Provider-agnostic cancel reason codes (mirror Tango RaaS v2 so they pass
 * straight through). Used to populate the cancel-reward dialog's dropdown.
 */
export type RewardCancelReason =
  | 'DELIVERY_INFO'
  | 'REWARD_AMOUNT'
  | 'REWARD_TYPE'
  | 'RECIPIENT'
  | 'CURRENCY'
  | 'RECIPIENT_REQUESTED'
  | 'RECIPIENT_OBLIGATIONS'
  | 'DUPLICATE'
  | 'FRAUD'
  | 'OTHER'

export const REWARD_CANCEL_REASONS: { value: RewardCancelReason; label: string }[] = [
  { value: 'DUPLICATE', label: 'Duplicate order' },
  { value: 'FRAUD', label: 'Fraud detected' },
  { value: 'DELIVERY_INFO', label: 'Incorrect delivery information' },
  { value: 'REWARD_AMOUNT', label: 'Incorrect reward amount' },
  { value: 'REWARD_TYPE', label: 'Incorrect reward type' },
  { value: 'RECIPIENT', label: 'Incorrect recipient' },
  { value: 'CURRENCY', label: 'Incorrect currency' },
  { value: 'RECIPIENT_REQUESTED', label: 'Recipient requested change' },
  { value: 'RECIPIENT_OBLIGATIONS', label: 'Recipient did not fulfil obligations' },
  { value: 'OTHER', label: 'Other' },
]

/** One row of the Rewards activity ledger (`GET /rewards/issuances/`). */
export interface RewardIssuanceRow {
  uuid: string
  resource_type: string
  resource_id: string
  /** Human-facing claim reference (CLM-XXXXXXXX) for claim-backed rewards; '' otherwise. */
  claim_reference: string
  provider: string
  /** Tango referenceOrderID - the id the tenant looks up in their Tango portal. */
  provider_order_id: string
  /** Tango referenceLineItemID. */
  provider_line_item_id: string
  /** Reward catalog item id (utid) the order was placed against. */
  utid: string
  /** Reward rule display name (as on Settings > Rewards); '' if none. */
  reward_rule: string
  amount: string
  currency: string
  recipient_email: string
  /** Denormalised recipient display name; '' when only an email was on file. */
  recipient_name: string
  status: RewardIssuanceStatus
  last_error_code: string
  last_error_message: string
  attempts: number
  ordered_at: string | null
  delivered_at: string | null
  redeemed_at: string | null
  cancelled_at: string | null
  /** Provider cancel reason code, set once the reward is cancelled; '' otherwise. */
  cancel_reason: string
  /** Whether the Cancel action should be offered (placed with provider, not yet redeemed/terminal). */
  is_cancellable: boolean
  created_at: string
}

/** `POST /rewards/issuances/<uuid>/cancel/`. */
export interface RewardIssuanceCancelPayload {
  reason_code: RewardCancelReason
  other_reason?: string
}
